// ═══════════════════════════════════════════════════════════════════════════════
// PWA shell height sync — Android pinch-zoom uses visualViewport.height.
// iOS mobile PWA uses CSS 100dvh only (no JS --fdh-visible-height lock).
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { isIosMobilePwa } from '../lib/pwaRuntime';
import { resolvePwaShellHeightPx, VIEWPORT_ZOOM_THRESHOLD } from '../lib/pwaShellHeight';

export const FDH_VISIBLE_HEIGHT_CSS_VAR = '--fdh-visible-height';
const VIEWPORT_ZOOMED_DATA_ATTR = 'data-fdh-viewport-zoomed';

const syncAndroidPwaVisibleViewportHeight = (): void => {
  const root = document.documentElement;
  root.style.setProperty(FDH_VISIBLE_HEIGHT_CSS_VAR, `${resolvePwaShellHeightPx()}px`);

  const scale = window.visualViewport?.scale ?? 1;
  if (scale > VIEWPORT_ZOOM_THRESHOLD) {
    root.setAttribute(VIEWPORT_ZOOMED_DATA_ATTR, 'true');
    return;
  }
  root.removeAttribute(VIEWPORT_ZOOMED_DATA_ATTR);
};

/** Sync --fdh-visible-height on <html> for Android PWA pinch-zoom; iOS uses 100dvh in CSS. */
export const usePwaVisibleViewportHeight = (enabled: boolean): void => {
  useEffect(() => {
    if (!enabled || isIosMobilePwa()) {
      return undefined;
    }

    const applySync = (): void => {
      syncAndroidPwaVisibleViewportHeight();
    };

    applySync();

    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', applySync);
    visualViewport?.addEventListener('scroll', applySync);
    window.addEventListener('resize', applySync);
    window.addEventListener('orientationchange', applySync);

    return () => {
      visualViewport?.removeEventListener('resize', applySync);
      visualViewport?.removeEventListener('scroll', applySync);
      window.removeEventListener('resize', applySync);
      window.removeEventListener('orientationchange', applySync);
      document.documentElement.style.removeProperty(FDH_VISIBLE_HEIGHT_CSS_VAR);
      document.documentElement.removeAttribute(VIEWPORT_ZOOMED_DATA_ATTR);
    };
  }, [enabled]);
};
